import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Función auxiliar para calcular una escala limpia con múltiplos naturales (500, 1000, 2000)
const calcularEscalaY = (valMax) => {
    const target = Math.max(valMax * 1.15, 100);
    const exp = Math.pow(10, Math.floor(Math.log10(target)));
    const fraction = target / exp;
    let niceMultiplier = 10;
    let interval = exp * 2;

    if (fraction <= 1.2) {
        niceMultiplier = 1.2;
        interval = exp * 0.3;
    } else if (fraction <= 1.5) {
        niceMultiplier = 1.5;
        interval = exp * 0.5;
    } else if (fraction <= 2) {
        niceMultiplier = 2;
        interval = exp * 0.5;
    } else if (fraction <= 3) {
        niceMultiplier = 3;
        interval = exp * 0.5;
    } else if (fraction <= 5) {
        niceMultiplier = 5;
        interval = exp * 1;
    } else {
        niceMultiplier = 10;
        interval = exp * 2;
    }

    const maxEje = Math.ceil(niceMultiplier * exp);
    return { max: maxEje, interval };
};

/**
 * Componente dedicado exclusivamente al renderizado y configuración de la Gráfica ECharts
 * para el consumo y órdenes de un distribuidor.
 */
const DistribuidorGraficaChart = ({
    categories = [],
    totalMesValues = [],
    promedioBloqueValues = [],
    promedioAnteriores3 = 0,
    visibleSeries = { mesActual: true, promedioUltimos3: true, promedioAnteriores3: true },
    isDarkMode = false,
    cargando = false,
    height = '210px'
}) => {
    const lineColor = '#6366f1'; // Morado corporativo

    const chartOptions = useMemo(() => {
        const series = [];

        // Serie 1: Curva Morada - PROMEDIO TRIMESTRAL
        if (visibleSeries.mesActual) {
            series.push({
                id: 'promedioTrimestral',
                name: 'Promedio Trimestral',
                type: 'line',
                data: promedioBloqueValues,
                smooth: 0.1,
                symbol: 'circle',
                symbolSize: 6,
                showSymbol: true,
                itemStyle: { color: lineColor },
                lineStyle: { width: 2.5, color: lineColor },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: `${lineColor}40` },
                        { offset: 1, color: `${lineColor}05` }
                    ])
                }
            });
        }

        // Serie 2: Línea punteada - Total Pagado del Mes (Verde)
        if (visibleSeries.promedioUltimos3) {
            series.push({
                id: 'totalMes',
                name: 'Total Pagado del Mes',
                type: 'line',
                data: totalMesValues,
                smooth: 0.2,
                symbol: 'circle',
                symbolSize: 6,
                showSymbol: true,
                itemStyle: { color: '#10b981' },
                lineStyle: { width: 2, type: 'dashed', color: '#10b981' }
            });
        }

        // Serie 3: Línea punteada de referencia Promedio 3 Meses Anteriores (Ámbar)
        if (visibleSeries.promedioAnteriores3 && categories.length > 0) {
            series.push({
                id: 'promedioAnteriores3',
                name: `Prom. 3 Meses Anteriores (${formatCurrency(promedioAnteriores3)})`,
                type: 'line',
                data: categories.map(() => Number(Number(promedioAnteriores3).toFixed(2))),
                showSymbol: false,
                itemStyle: { color: '#f59e0b' },
                lineStyle: { width: 2, type: 'dashed', color: '#f59e0b' }
            });
        }

        // Calcular valor máximo para el eje Y
        const maxGrafica = Math.max(
            ...totalMesValues,
            ...promedioBloqueValues,
            Number(promedioAnteriores3 ?? 0),
            100
        );

        const escalaY = calcularEscalaY(maxGrafica);

        return {
            tooltip: {
                trigger: 'axis',
                confine: true,
                backgroundColor: isDarkMode ? '#121e2d' : '#ffffff',
                borderColor: isDarkMode ? '#233348' : '#e2e8f0',
                borderWidth: 1,
                textStyle: { color: isDarkMode ? '#f8fafc' : '#0f172a' },
                padding: [8, 12],
                extraCssText: isDarkMode
                    ? 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5); border-radius: 8px;'
                    : 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12); border-radius: 8px;',
                formatter: (params) => {
                    if (!params || !params.length) return '';
                    const mes = params[0].name;
                    const dataIndex = params[0].dataIndex;

                    // Variación del mes actual contra el mes anterior en la gráfica
                    let momBadge = '';
                    if (dataIndex > 0) {
                        const valAct = totalMesValues[dataIndex];
                        const valAnt = totalMesValues[dataIndex - 1];
                        const mesAnt = categories[dataIndex - 1];
                        if (valAnt > 0) {
                            const pct = ((valAct - valAnt) / valAnt) * 100;
                            const esPos = pct >= 0;
                            const color = esPos ? '#10b981' : '#ef4444';
                            const flecha = esPos ? '▲' : '▼';
                            const signo = esPos ? '+' : '';
                            momBadge = `<span style="color: ${color}; font-size: 0.70rem; font-weight: 600; margin-left: 6px;">(${flecha} ${signo}${pct.toFixed(1)}% vs ${mesAnt})</span>`;
                        }
                    }

                    let html = `<div style="font-size: 0.80rem; font-weight: 700; margin-bottom: 4px; color: ${isDarkMode ? '#f8fafc' : '#0f172a'};">Período: ${mes}</div>`;

                    params.forEach((p) => {
                        const esTotal = p.seriesId === 'totalMes' || p.seriesName === 'Total Pagado del Mes';
                        html += `
                            <div style="font-size: 0.75rem; display: flex; align-items: center; gap: 6px; margin-bottom: 2px;">
                                ${p.marker}
                                <span style="display: flex; align-items: center;">
                                    <b style="color: ${isDarkMode ? '#f8fafc' : '#0f172a'};">${formatCurrency(p.value)}</b>
                                    ${esTotal ? momBadge : ''}
                                </span>
                            </div>
                        `;
                    });

                    return html;
                }
            },
            grid: {
                top: 20,
                right: 15,
                bottom: 25,
                left: 10,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: categories,
                boundaryGap: false,
                axisLine: { lineStyle: { color: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0' } },
                axisTick: { show: false },
                axisLabel: {
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    fontSize: 10.5
                }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: escalaY.max,
                interval: escalaY.interval,
                splitLine: {
                    lineStyle: {
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9',
                        type: 'dashed'
                    }
                },
                axisLabel: {
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    fontSize: 10.5,
                    formatter: (val) => {
                        if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
                        if (val >= 100000) return `$${(val / 1000).toFixed(0)}k`;
                        return `$${Number(val).toLocaleString('es-MX')}`;
                    }
                }
            },
            series
        };
    }, [
        categories,
        totalMesValues,
        promedioBloqueValues,
        promedioAnteriores3,
        visibleSeries,
        isDarkMode,
        lineColor
    ]);

    if (cargando) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height }}>
                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                <span className="text-muted small">Cargando gráfica...</span>
            </div>
        );
    }

    return (
        <ReactEChartsCore
            echarts={echarts}
            option={chartOptions}
            notMerge={true}
            lazyUpdate={false}
            style={{ height, width: '100%' }}
        />
    );
};

DistribuidorGraficaChart.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.string),
    totalMesValues: PropTypes.arrayOf(PropTypes.number),
    promedioBloqueValues: PropTypes.arrayOf(PropTypes.number),
    promedioAnteriores3: PropTypes.number,
    visibleSeries: PropTypes.shape({
        mesActual: PropTypes.bool,
        promedioUltimos3: PropTypes.bool,
        promedioAnteriores3: PropTypes.bool
    }),
    isDarkMode: PropTypes.bool,
    cargando: PropTypes.bool,
    height: PropTypes.string
};

export default DistribuidorGraficaChart;
